/**
 * @file BaseDevice.ts
 * @author Rapoo Device SDK Team
 * @date 2026-01-05
 * @description 设备驱动基类
 *              所有具体设备驱动 (如 VT9, V300 等) 均需继承此类
 *              负责：通道管理、生命周期、指令队列、Feature 管理
 * @dependencies HidChannel, DeviceManifest, CommandQueue
 */

import type { HidChannel } from '../channels/hid/HidChannel'
import { DeviceManifest, Role, RoleId, type usageType } from '../types/manifest'
import { EventEmitter } from 'events'
import { DeviceFeatureMap } from '../features/FeatureMap'
import { CommandQueue } from './CommandQueue'
import { Config, UserCustomData } from '../types/config'
import { DeviceSession } from './session/DeviceSession'
import { ConnectionType } from '../types/connection'
import { OutputReportPollingExchange } from '../channels/hid/exchange/OutputReportPollingExchange'
import { InflightRegistry } from '../cache/InflightRegistry'
import { HardwareCache } from '../cache/HardwareCache'
import { TaggedCache } from '../cache/CacheTags'
import { DeviceError, DeviceErrorCode, isTransportDeviceError } from '../errors/DeviceError'
import { DeviceEvents } from '../events/DeviceEvents'
import { DEFAULT_FIRMWARE_VERSION_POLICY, type FirmwareVersionPolicy } from '../types/firmware'

/**
 * 设备驱动基类 (Driver Base)
 * 所有具体的设备类 (e.g. VT9Device) 都要继承此类
 *
 * 职责：
 * 1. 持有 HidChannel 和 Manifest
 * 2. 处理通用的生命周期 (open, close, onConnect)
 * 3. 作为 Feature 的容器
 * 4. 管理指令发送队列
 */
export abstract class BaseDevice extends EventEmitter {
  /**
   * 唯一标识符
   * 用于在 UI 中作为 key
   */
  public readonly id: string = Math.random().toString(36).slice(2, 9)

  /**
   * sdk版本号
   * 用于云同步等功能维护兼容处理
   */
  public readonly sdkVersion: string = '0.0.0'

  /**
   * 功能容器 (Feature Container)
   * 存储该设备支持的所有功能模块。
   * 使用 Partial<DeviceFeatureMap> 表示并非所有功能都存在。
   */
  public readonly features: Partial<DeviceFeatureMap> = {}

  /**
   * 指令队列
   * 保证指令有序发送，避免并发冲突
   */
  protected queue: CommandQueue

  /**
   * 进行中请求登记表 (Phase 1 - InflightRegistry)
   *
   * 用途:同一 `(address, length)` 的并发 `sendRead` 调用复用同一个 Promise,
   * 避免对硬件发出重复的并发请求。每个 `BaseDevice` 实例持有一个独立的登记表,
   * 与缓存的启用状态(`HardwareCache.disable()`)无关(对应 Requirement 1.6)。
   *
   * @internal 仅供 SDK 内部 (`utils/common.ts` 的 `createSendRead`) 使用,
   *           业务侧请勿直接访问。
   */
  readonly inflight: InflightRegistry = new InflightRegistry()

  /**
   * 会话级硬件缓存 (Phase 3 - TaggedCache 装饰器)
   *
   * 用途:在一次连接生命周期内,以 `(address, length)` 为 key 缓存 `sendAndWait`
   * 返回的字节快照。命中时基于 `entry.data` 重建 `DataView` 喂给 parser、绕过
   * `CommandQueue`,消除业务侧重复进入页面时的硬件读取(对应 Requirement 2.1 / 2.2)。
   *
   * - 每个 `BaseDevice` 实例持有独立缓存,跨设备互不影响。
   * - `public readonly`:业务侧可调 `device.cache.disable()` / `size()` 等观察接口
   *   (Requirement 5.11);`utils/common.ts` 的自由函数需按 `device.cache.get(...)` 访问。
   * - `close()` 内 **先于通道关闭** 调用 `this.cache.clearAll()`(Requirement 2.6 / 2.10)。
   */
  public readonly cache: TaggedCache = new TaggedCache(new HardwareCache())

  /**
   * 设备运行时会话。
   * 新架构中 usage/channel/protocol 都应通过 session 归一管理。
   */
  public readonly session: DeviceSession

  /**
   * 连接类型 (用于协议层区分通信方式)
   * 0xFF: 有线 (USB)
   * 0xA5: 无线 (Receiver)
   * 默认为 0xFF
   */
  public connectionType: number = 0xff

  /**
   * 获取语义化的连接类型
   *
   * 业务层通过此方法获取连接类型，无需关心底层原始数值。
   * 各设备驱动子类可根据自身情况重写此方法，覆盖默认转换规则。
   *
   * 默认规则：
   *  - connectionType === 0xa5 → ConnectionType.Wireless（标准 2.4G 无线）
   *  - 其他值（包括 0xff）     → ConnectionType.Wired（有线 / 兜底）
   */
  public getConnectionType(): ConnectionType {
    return this.connectionType === 0xa5 ? ConnectionType.Wireless : ConnectionType.Wired
  }

  /*
   * 子设备
   * 子设备列表，用于存储该设备下的子设备
   */
  public readonly subDevices?: BaseDevice[] = []

  /*
   * 父设备
   * 父设备列表，用于存储该设备下的子设备
   */
  public readonly parentDevice?: BaseDevice = undefined

  /**
   * 当前是否为硬件管理员模式
   * 通过 enterAdminMode/exitAdminMode 控制，仅支持硬件 admin 的设备使用
   */
  public adminMode: boolean = false

  /**
   * 主通道语义：日常为 `default`；独立 Bootloader 枚举建连时为 `update`。
   * 由 RapooDeviceManager 在创建设备后写入。
   */
  public primaryUsageType: usageType = 'default'

  /** 主通道是否为固件升级（Boot）模式设备 */
  public isFirmwareUpdateMode(): boolean {
    return this.primaryUsageType === 'update'
  }

  /**
   * 当前配置索引转换使用的角色列表（有 configSlots 的设备用此角色匹配槽位可见性）
   * 由外部（web 层）通过 setConfigSlotRoles 设置
   */
  public configSlotRoles: Role[] = [RoleId.user]

  /**
   * 设置配置角色列表
   * @param roles 用户角色列表，如 [RoleId.superAdmin, RoleId.admin]
   */
  public setConfigSlotRoles(roles: Role[]): void {
    this.configSlotRoles = roles
  }

  /**
   * 固件升级文件 base URL。
   * 由应用层在 `RapooDeviceManager.getInstance(manifests, options)` 时注入；
   * chips / feature 通过 `device.getFirmwareBaseUrl()` 取得当前渠道对应的下载源，
   * 避免在芯片实现中引入应用层概念。
   */
  private firmwareBaseUrl: string = 'https://soft.rapoo.com/RapooDriver/Firmware'

  /** 固件版本资格策略；与固件下载源一样由应用层显式注入。 */
  private firmwareVersionPolicy: FirmwareVersionPolicy = DEFAULT_FIRMWARE_VERSION_POLICY

  public getFirmwareBaseUrl(): string {
    return this.firmwareBaseUrl
  }

  public getFirmwareVersionPolicy(): FirmwareVersionPolicy {
    return this.firmwareVersionPolicy
  }

  /**
   * 设备创建后由 `RapooDeviceManager` 注入固件 base URL。
   * @internal 仅供 SDK 内部使用
   */
  public __setFirmwareBaseUrl(url: string): void {
    this.firmwareBaseUrl = url
  }

  /**
   * 设备创建后由 `RapooDeviceManager` 注入版本资格策略。
   * 关键：运行时收到非法值时必须回落严格策略，避免误放开降级。
   * @internal 仅供 SDK 内部使用
   */
  public __setFirmwareVersionPolicy(policy: FirmwareVersionPolicy): void {
    this.firmwareVersionPolicy = policy === 'any-version' ? policy : DEFAULT_FIRMWARE_VERSION_POLICY
  }

  /**
   * 当前是管理员账号
   */
  public isAdminAccount(): boolean {
    return (
      this.configSlotRoles.includes(RoleId.admin) ||
      this.configSlotRoles.includes(RoleId.superAdmin)
    )
  }

  /*
   * 当前使用的配置，默认为第一个配置
   * index: 配置索引
   * name: 配置名称
   *
   * 使用 getter/setter 实现自动缓存失效(对应 Requirement 3.9):
   * 当 currentConfig 被切换且 index 变化时,setter 自动调用
   * `cache.invalidateByTag('cfg:<oldIndex>')` 清空旧配置相关缓存,业务层无需感知。
   */
  private _currentConfig: Config = {
    index: 0,
    name: '出厂配置',
  }

  public get currentConfig(): Config {
    return this._currentConfig
  }

  public set currentConfig(newConfig: Config) {
    const oldIndex = this._currentConfig.index
    this._currentConfig = newConfig
    if (oldIndex !== newConfig.index) {
      this.cache.invalidateByTag(`cfg:${oldIndex}`)
    }
  }
  /*
   * 配置列表
   */
  public configList: Config[] = []
  /**
   * 实时上报用户自定义状态
   */
  public userCustomData?: UserCustomData

  constructor(
    public readonly channel: HidChannel,
    public readonly manifest: DeviceManifest,
  ) {
    super()
    // 初始化指令队列，默认间隔 10ms；传输失败时向业务层广播，便于配置页感知休眠/断连
    this.queue = new CommandQueue({
      interval: 10,
      onError: (error) => {
        if (isTransportDeviceError(error)) {
          this.emit(DeviceEvents.IoFailed, error)
        }
      },
    })
    this.session = new DeviceSession({
      owner: this,
      manifest,
      primaryChannel: channel,
    })
    // 监听底层上报，转发给子类处理
    this.channel.onInputReport(this.handleReport.bind(this))

    // 启用 Tag 审计,在 close() 时输出配对报告
    // 运行时开销仅为 Set<Tag>.add,可忽略不计
    this.cache.__enableTagAudit(true)
  }

  /**
   * 执行原子操作
   * 将一个包含多个步骤（如发送+等待响应）的任务作为一个整体加入队列
   * 保证任务执行期间不会插入其他指令
   *
   * @param task 需要原子执行的任务，接收 channel 实例作为参数
   */
  async runAtomic<T>(task: (channel: HidChannel) => Promise<T>): Promise<T> {
    return this.queue.add(() => task(this.channel))
  }

  /**
   * 让当前设备复用另一个设备的命令队列。
   * 关键：逻辑子设备和父设备如果共用同一条物理 HID，必须共用队列，避免 send/getFeatureReport 串包。
   */
  public shareCommandQueueWith(owner: BaseDevice): void {
    this.queue = owner.queue
  }

  /**
   * 发送指令并等待 Feature Report 响应
   * 封装了 runAtomic 和 轮询重试逻辑
   *
   * @param outputReportId 发送的 Report ID
   * @param outputData 发送的数据
   * @param inputReportId 期望读取的 Feature Report ID
   * @param validate 验证响应数据是否有效 (返回 true: 有效, false: 忙碌/重试)
   * @param options 配置项 (超时时间、重试次数)
   * @returns 响应数据 (DataView)
   */
  async sendAndWait(
    outputReportId: number,
    outputData: Uint8Array,
    inputReportId: number,
    validate: (data: DataView) => boolean = () => true,
    options: {
      timeout?: number
      maxRetries?: number
      skipCmdOffset?: boolean
      /** 默认通知传输失败；false 时仍抛出异常，但不广播本次请求的 IoFailed。 */
      notifyIoFailure?: boolean
    } = {},
  ): Promise<DataView> {
    const exchange = new OutputReportPollingExchange(this.channel, {
      queue: (task) => this.queue.add(task, { notifyIoFailure: options.notifyIoFailure }),
      getDelayMs: () => {
        // 关键：硬件实测有线最短 1ms，无线最短 10ms，过快读取会拿到忙碌 ACK。
        return this.getConnectionType() === ConnectionType.Wired ? 1 : 10
      },
    })

    return exchange.request({
      reportId: outputReportId,
      payload: outputData,
      responseReportId: inputReportId,
      validate,
      timeout: options.timeout,
      maxRetries: options.maxRetries,
    })
  }

  /**
   * 中断传输时等待设备主动上报数据
   * @param timeoutMs 超时时间，单位毫秒
   * @returns 返回首个上报的 reportId 和数据内容
   */
  private waitForNextInputReport(timeoutMs = 500): Promise<{ reportId: number; data: DataView }> {
    return new Promise((resolve, reject) => {
      const onReport = (reportId: number, data: DataView) => {
        cleanup()
        resolve({ reportId, data })
      }

      const timer = window.setTimeout(() => {
        cleanup()
        reject(new DeviceError(DeviceErrorCode.TIMEOUT, `等待 input report 超时: ${timeoutMs}ms`))
      }, timeoutMs)

      const cleanup = () => {
        window.clearTimeout(timer)
        this.channel.offInputReport(onReport)
      }

      this.channel.onInputReport(onReport)
    })
  }

  async sendAndReceive(
    outputReportId: number,
    outputData: Uint8Array,
    _inputReportId: number,
    timeoutMs: number,
  ): Promise<{ reportId: number; data: DataView }> {
    return this.runAtomic(async () => {
      const pendingInputReport = this.waitForNextInputReport(timeoutMs)
      try {
        await this.channel.sendOutputReport(outputReportId, outputData)
      } catch (error) {
        // 写入失败时仍需消费预先注册的等待 Promise，避免其超时后形成未处理异常。
        void pendingInputReport.catch(() => undefined)
        throw error
      }
      return pendingInputReport
    })
  }

  /**
   * 打开设备
   */
  async open(): Promise<void> {
    await this.channel.open()
    this.emit('connected')
    // 辅助通道（固件升级、配对等）只负责对应的原始协议通信，不能触发
    // default 控制通道上的配置、DPI 等业务初始化读写。
    if (this.primaryUsageType === 'default') {
      this.onConnected()
    } else {
      console.log(
        `[BaseDevice] Skipping onConnected for auxiliary channel: model=${this.manifest.model}, usageType=${this.primaryUsageType}`,
      )
    }
  }

  /**
   * 关闭设备
   *
   * 顺序约束(对应 Requirement 2.6 / 2.10):
   * 1. 先 `this.cache.clearAll()` —— 在通道关闭之前清空缓存,连接断开后无残留字节快照,
   *    与"跨刷新 / 重连视为冷启动,不持久化"契约一致。
   * 2. 再关闭 session 通道与主通道。
   *
   * --- Tag 审计输出 ---
   * 在 `clearAll()` 之前输出 tag 配对审计报告,帮助开发者发现未配对的 tag。
   */
  async close(): Promise<void> {
    // clearAll 之前输出 tag 审计报告
    const report = this.cache.getTagAuditReport()
    if (report.registeredOnly.length > 0 || report.invalidatedOnly.length > 0) {
      console.group('[TaggedCache] Tag Audit Report for device', this.id)
      if (report.registeredOnly.length > 0) {
        console.warn(
          `  ⚠️  Registered only (never invalidated): [${report.registeredOnly.join(', ')}]`,
        )
      }
      if (report.invalidatedOnly.length > 0) {
        console.warn(
          `  ⚠️  Invalidated only (never registered): [${report.invalidatedOnly.join(', ')}]`,
        )
      }
      console.groupEnd()
    }

    // 必须先于通道关闭执行(Requirement 2.6)
    this.cache.clearAll()
    await this.session.channels.closeAll()
    await this.channel.close()
    this.emit('disconnected')
    this.onDisconnected()
  }

  /**
   * 钩子：连接成功后触发 (子类可覆盖，用于初始化读取)
   */
  protected onConnected() {}

  /**
   * 钩子：连接断开后触发
   */
  protected onDisconnected() {}

  /**
   * 设备连接状态检查，默认都视为正常连接上了
   * 可自行决定如何判定是否是正常连接状态
   */
  public checkConnect(): Promise<boolean> {
    return Promise.resolve(true)
  }

  /**
   * 处理底层上报数据
   */
  protected handleReport(reportId: number, data: DataView) {
    // 将 DataView 转换为 Uint8Array 以便上层统一处理
    const buffer = new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
    this.emit('report', reportId, buffer)
  }
}
